import { useNavigate } from "react-router-dom";
import { X, Bookmark, ThumbsUp, Zap } from "lucide-react";

interface SignupNudgeProps {
  open: boolean;
  onClose: () => void;
  action?: "save" | "like" | "dislike";
}

const ACTION_COPY = {
  save: {
    icon: Bookmark,
    headline: "Save this episode to your library",
    sub: "Create a free account to build your personal podcast collection — save episodes, create playlists, and get notified when new content drops.",
  },
  like: {
    icon: ThumbsUp,
    headline: "Help us find better episodes for you",
    sub: "Sign in to rate results and train your recommendations. The more you vote, the sharper your results get.",
  },
  dislike: {
    icon: ThumbsUp,
    headline: "Help us find better episodes for you",
    sub: "Sign in to rate results and train your recommendations. The more you vote, the sharper your results get.",
  },
};

export default function SignupNudge({ open, onClose, action = "save" }: SignupNudgeProps) {
  const navigate = useNavigate();
  const copy = ACTION_COPY[action];
  const Icon = copy.icon;

  if (!open) return null;

  // Capture the current URL (path + query + hash) so login can return the
  // user to the exact same search results page they were on when prompted.
  const currentPath =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search + window.location.hash
      : "/";
  const goToLogin = (mode?: "signup") => {
    const params = new URLSearchParams();
    if (mode) params.set("mode", mode);
    params.set("redirect", currentPath);
    navigate(`/login?${params.toString()}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-sm modal-shell animate-scale-in">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors z-10"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 text-center">
          <div className="w-12 h-12 mx-auto mb-4 bg-primary/10 flex items-center justify-center retro-border rounded-md">
            <Icon className="w-6 h-6 text-primary" />
          </div>

          <h3 className="font-retro font-bold text-lg mb-2">{copy.headline}</h3>
          <p className="font-mono text-xs text-muted-foreground leading-relaxed mb-6">
            {copy.sub}
          </p>

          <button
            onClick={() => goToLogin("signup")}
            className="btn-comic-primary w-full mb-3"
          >
            <Zap className="w-4 h-4" />
            Create Free Account
          </button>

          <button
            onClick={() => goToLogin()}
            className="btn-comic-ghost btn-comic-sm w-full"
          >
            Already have an account? Sign in
          </button>
        </div>
      </div>
    </div>
  );
}
